import React, { useState } from "react";
import { compileTournamentElo } from "services/rankingService";
import { fetchUsersByIds } from "services/userService";
import { getMatchesByTournamentId } from "services/matchService";

const CompileTournament = ({ tournament }) => {
  const [loading, setLoading] = useState(false);
  const [playerMatches, setPlayerMatches] = useState({});
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [success, setSuccess] = useState(false);
  const [search, setSearch] = useState("");

  const handlePreview = async () => {
    if (!tournament?.id || !tournament?.locationId) return;
    setLoading(true);
    setPlayerMatches({});
    setSuccess(false);

    try {
      const fetchedMatches = await compileTournamentElo(tournament, false);
      const userIds = Object.keys(fetchedMatches);
      const fetchedUsers = await fetchUsersByIds(userIds);
      const fetchedTournamentMatches = await getMatchesByTournamentId(tournament.id);

      setPlayerMatches(fetchedMatches);
      setUsers(fetchedUsers);
      setMatches(fetchedTournamentMatches);
      setConfirming(true);
    } catch (error) {
      console.error("Erreur lors de la préparation de la compilation:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await compileTournamentElo(tournament, true);
      setSuccess(true);
    } catch (error) {
      console.error("Erreur lors de la compilation du tournoi:", error);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.id === userId);
    return user ? user.name : userId;
  };

  const getMatchDetails = (matchId) => {
    const match = matches.find((m) => m.id === matchId);
    if (!match) return `Match ${matchId.slice(0, 6)}`;
    return `${match.team1?.players?.map((p) => p.name).join(" & ")} -vs- ${match.team2?.players?.map((p) => p.name).join(" & ")}`;
  };

  const filteredPlayerIds = Object.keys(playerMatches).filter((userId) =>
    getUserName(userId).toLowerCase().includes(search.toLowerCase())
  );

  const calculateTotalDelta = (matches) =>
    matches.reduce((sum, match) => sum + match.delta, 0);

  if (confirming) {
    return (
      <div className="bg-gray-700 w-full md:w-2/3 p-6 rounded-lg shadow-lg text-white mx-auto">
        <h2 className="text-2xl mb-4 font-bold text-center">
          {tournament?.compiled || success ? "✅ Compilation réussie" : "Vérification de la compilation"}
        </h2>

        <input
          type="text"
          placeholder="Rechercher un joueur..."
          className="mb-6 p-2 w-full rounded bg-gray-600 text-white"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <table className="w-full text-sm mb-6 table-auto">
          <thead>
            <tr className="border-b border-gray-600">
              <th className="p-3 text-center">Joueur</th>
              <th className="p-3 text-center">Total Différence</th>
              <th className="p-3 text-center">Match</th>
              <th className="p-3 text-center">Différence</th>
            </tr>
          </thead>
          <tbody>
            {filteredPlayerIds.length > 0 ? (
              filteredPlayerIds.map((userId) => {
                const matchesPlayed = playerMatches[userId];
                const totalDelta = calculateTotalDelta(matchesPlayed);

                return (
                  <React.Fragment key={userId}>
                    <tr className="border-b border-gray-600 bg-gray-800">
                      <td className="p-3 font-bold text-base text-center" rowSpan={matchesPlayed.length + 1}>
                        {getUserName(userId)}
                      </td>
                      <td
                        className={`p-3 font-bold text-base text-center ${
                          totalDelta >= 0 ? "text-green-400" : "text-red-400"
                        }`}
                        rowSpan={matchesPlayed.length + 1}
                      >
                        {totalDelta >= 0 ? `+${totalDelta}` : totalDelta}
                      </td>
                    </tr>
                    {matchesPlayed.map((m, idx) => (
                      <tr key={idx} className="border-b border-gray-500">
                        <td className="p-2 text-center">{getMatchDetails(m.matchId)}</td>
                        <td
                          className={`p-2 text-center font-semibold ${
                            m.delta >= 0 ? "text-green-400" : "text-red-400"
                          }`}
                        >
                          {m.delta >= 0 ? `+${m.delta}` : m.delta}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="4" className="text-center p-6">
                  Aucun joueur trouvé.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {!tournament?.compiled && !success && (
          <div className="flex justify-center">
            <button
              onClick={handleConfirm}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded text-lg"
              disabled={loading}
            >
              Confirmer la compilation
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={handlePreview}
      disabled={loading}
      className={`${
        tournament?.compiled ? "bg-purple-800 hover:bg-purple-900" : "bg-blue-600 hover:bg-blue-700"
      } text-white px-6 py-3 rounded text-lg mx-auto block`}
    >
      {loading
        ? "Chargement..."
        : tournament?.compiled
        ? "Voir le résumé de la compilation"
        : "Compiler les points"}
    </button>
  );
};

export default CompileTournament;
